import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, SimpleWizard, SimpleWizardStep, SimpleWizardConfirmStep } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { cancelOrder } from '@/services/orders';
import { Radio } from '@/components/forms/Radio';
import theme from './OrderCancelDialog.module.scss';
import Banner from '@/components/Banner';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OrderListItem from '../OrderListItem';
import { OrderCloseDialogProps } from '.';

interface OptionOrderCancelDialogProps extends OrderCloseDialogProps {}

enum Step {
    SelectCancelType = 1,
    Confirm = 2,
    Canceled = 3,
}

const OptionOrderCancelDialog: React.FC<OptionOrderCancelDialogProps> = ({ show, onClose, onOrderCancel, order }) => {
    const [step, setStep] = useState(Step.SelectCancelType);
    const [cancelMode, setCancelMode] = useState<'Simple' | 'Reopen'>('Simple');
    const reset = () => {
        setCancelMode('Reopen');
        setStep(Step.SelectCancelType);
    };

    const handleOpen = () => {
        reset();
    };

    const handleSubmit = async () => {
        if (cancelMode === 'Simple') await cancelOrder(order.id);
        if (cancelMode === 'Reopen') await cancelOrder(order.id, true);
        triggerHandler(onOrderCancel, [order.id]);
    };

    const handleFinish = () => {
        triggerHandler(onClose);
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} size="xl" onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Cancel order</Trans>
            </ModalHeader>
            <ModalBody>
                <SimpleWizard
                    onCancel={step !== Step.Canceled ? handleCancelClick : undefined}
                    steps={[Step.SelectCancelType, Step.Confirm, Step.Canceled]}
                    currentStep={step}
                    onStepChange={setStep}
                    onSubmit={handleSubmit}
                    onFinish={handleFinish}
                    saveButtonProps={{ children: <Trans>Cancel order</Trans> }}>
                    <SimpleWizardStep stepNumber={Step.SelectCancelType} header={<Trans>Choose cancel type</Trans>}>
                        <div className={theme.cancelMode}>
                            <Radio
                                labelClassName={theme.cancelModeRadio}
                                name="reopen"
                                checked={cancelMode === 'Reopen'}
                                onChange={(e) => setCancelMode('Reopen')}>
                                <span className={theme.title}>
                                    <Trans>Cancel order and reopen the buyer option</Trans>
                                </span>
                                <span className={theme.body}>
                                    <Trans>
                                        The existing order will be cancelled and the buyer option will be reopened to the
                                        buyer.
                                    </Trans>
                                </span>
                            </Radio>
                            <Radio
                                labelClassName={theme.cancelModeRadio}
                                name="standard"
                                checked={cancelMode === 'Simple'}
                                onChange={(e) => setCancelMode('Simple')}>
                                <span className={theme.title}>
                                    <Trans>Cancel order</Trans>
                                </span>
                                <span className={theme.body}>
                                    <Trans>The existing order will be cancelled, without confirmation from the buyer.</Trans>
                                </span>
                            </Radio>
                        </div>
                    </SimpleWizardStep>
                    <SimpleWizardStep
                        stepNumber={Step.Confirm}
                        header={<Trans>Confirm cancellation of order #{order.id}</Trans>}>
                        <div className={theme.topText}>
                            <Trans>
                                You are about to cancel this order for buyer option {order.buyerOptionName}, this action is
                                not reversible, are you sure you want to do this?
                            </Trans>
                        </div>
                        {cancelMode === 'Simple' && (
                            <Banner type="warning" className={theme.warning}>
                                <Trans>
                                    <b>Warning</b> Order #{order.id} will be cancelled, this will mean that will not be an
                                    order for {order.buyerOptionName} and the buyer will not be able to make a new choice.
                                </Trans>
                            </Banner>
                        )}

                        <OrderListItem order={order} onChange={() => null} readOnly />
                        {cancelMode === 'Simple' && (
                            <Banner type="info">
                                <Trans>
                                    <b>Please note</b> cancelling an order means that this order will be cancelled straight
                                    away, without any input from the buyer; this is usually done when the buyer has chosen a
                                    different product in a change request and the order is not valid anymore.
                                </Trans>
                            </Banner>
                        )}

                        {cancelMode === 'Reopen' && (
                            <Banner type="info">
                                <Trans>
                                    <b>Please note</b> cancelling an order and reopening the option is means that this order
                                    will be cancelled straight away, and the buyer will be able to make a new choice; this is
                                    usually done when the buyer would like to make another choice after submitting the
                                    choices and you would like to let the buyer do that.
                                </Trans>
                            </Banner>
                        )}
                    </SimpleWizardStep>

                    <SimpleWizardConfirmStep
                        stepNumber={Step.Canceled}
                        header={
                            <>
                                {cancelMode === 'Simple' && <Trans>Cancellation of order {order.id} confirmed</Trans>}
                                {cancelMode === 'Reopen' && (
                                    <Trans>
                                        Cancellation of order #{order.id}, and, reopening of buyer option{' '}
                                        {order.buyerOptionGroupName} confirmed
                                    </Trans>
                                )}
                            </>
                        }>
                        <div className={theme.confirmed}>
                            <FontAwesomeIcon height="124px" width="124px" icon={faCheckCircle} className={theme.icon} />
                            <br />

                            {cancelMode === 'Simple' && <Trans>Order #{order.id} has been cancelled.</Trans>}
                            {cancelMode === 'Reopen' && (
                                <Trans>
                                    Order #{order.id} cancelled and {order.buyerOptionGroupName} reopened successfully.
                                </Trans>
                            )}
                        </div>
                    </SimpleWizardConfirmStep>
                </SimpleWizard>
            </ModalBody>
        </Modal>
    );
};
export default OptionOrderCancelDialog;